/**
 * 🔴 **Forma canônica do pedido — a única normalização de idempotência do produto.**
 *
 * Ela existe compartilhada, e não copiada dos dois lados, porque os dois lados PRECISAM concordar:
 *
 * - o **servidor** a usa para o `idempotencyFingerprint` (`buildOrderFingerprint`), que decide, na
 *   violação do índice único, entre replay legítimo (`200` com o pedido original) e reuso da chave
 *   com outro carrinho (`409 IDEMPOTENCY_KEY_REUSE`);
 * - o **cliente** a usa para decidir quando a chave de idempotência do carrinho morre.
 *
 * Se as duas normalizações divergirem, cada divergência vira um dos dois defeitos que a idempotência
 * existe para impedir: cliente MAIS tolerante que o servidor guarda a chave para um carrinho que o
 * servidor considera outro — `409` que não passa nunca; cliente MENOS tolerante gera chave nova para
 * um carrinho que o servidor considera o mesmo — **segundo pedido impresso na cozinha**. Uma função
 * só transforma "concordar" em propriedade estrutural, em vez de disciplina.
 *
 * 🔴 **A serialização é contrato PERSISTIDO.** `Order.idempotencyFingerprint` guarda o hash desta
 * string, então mudar a ordem das chaves, o nome de um campo ou o critério de ordenação **invalida o
 * fingerprint de todo pedido já gravado**: o retry de um pedido em voo durante o deploy deixaria de
 * casar e viraria `409` num replay legítimo. Alterar aqui é migração de dado, não refactor.
 */

/** Item na forma mínima que a idempotência enxerga — preço e nome não entram, o servidor os resolve. */
export interface NormalizableOrderItem {
  productId: string;
  quantity: number;
  optionIds?: string[];
}

export interface NormalizableOrderPayload {
  items: NormalizableOrderItem[];
  customerName?: string | null;
}

/**
 * Forma canônica de `(contexto, carrinho)` como string estável.
 *
 * `contextId` é o que amarra a chave ao lugar: no servidor é o `locationId`; no cliente, o `slug` da
 * mesa. O valor não precisa ser o mesmo dos dois lados — cada um compara consigo mesmo, e dentro de
 * um carrinho ele é constante. O que precisa ser igual é **todo o resto**.
 *
 * O que a normalização decide, e por quê:
 *
 * - **ordem de item e de opcional não é informação**: o mesmo carrinho reserializado por um retry não
 *   pode virar "carrinho diferente";
 * - **opcional repetido não muda o pedido** (o serviço deduplica no snapshot pelo mesmo motivo);
 * - **nome ausente e string vazia descrevem o mesmo pedido**, e o cliente pode enviar os dois entre
 *   retries.
 */
export function normalizeOrderPayload(payload: NormalizableOrderPayload, contextId: string): string {
  const items = payload.items
    .map((item) => ({
      productId: item.productId,
      quantity: item.quantity,
      optionIds: [...new Set(item.optionIds ?? [])].sort(),
    }))
    .sort((left, right) => {
      const byProduct = left.productId.localeCompare(right.productId);
      if (byProduct !== 0) {
        return byProduct;
      }
      const byOptions = left.optionIds.join(',').localeCompare(right.optionIds.join(','));
      return byOptions !== 0 ? byOptions : left.quantity - right.quantity;
    });

  // ⚠️ A ordem destas chaves é o contrato persistido descrito no topo: `locationId` primeiro,
  // `customerName` depois, `items` por último. Não reordene.
  return JSON.stringify({
    locationId: contextId,
    customerName: payload.customerName?.trim() || null,
    items,
  });
}
